import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, X, LogOut } from "lucide-react";

const DISMISS_KEY = "quanta.impersonation_banner_dismissed";

/**
 * Phase 23.1 — Impersonation banner.
 *
 * Renders a persistent yellow strip at the top of AppShell when a
 * platform admin has switched into a tenant they don't have a real
 * ``users`` membership in (`is_impersonating: true` on /auth/me).
 *
 * Exit strategy:
 *  1. Try switching to the identity's last real-membership tenant
 *     (identity.last_tenant_id if it maps to an actual membership).
 *  2. If no real memberships → clean logout + redirect to /login.
 */
export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const { switchTenant, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch (_e) {}
  };
  useEffect(() => { load(); }, []);

  if (!me?.is_impersonating || dismissed) return null;

  const memberships = me?.memberships || [];
  const realMemberships = memberships.filter((m) => m.tenant_id !== me?.current_tenant_id);

  const exit = async () => {
    setBusy(true);
    try {
      // 1. Prefer identity.last_tenant_id if it maps to a real membership
      //    other than the current impersonated one.
      const preferred = me?.identity?.last_tenant_id;
      let target = null;
      if (preferred && preferred !== me.current_tenant_id) {
        target = realMemberships.find((m) => m.tenant_id === preferred);
      }
      // 2. Fall back to any real membership.
      if (!target && realMemberships.length > 0) {
        target = realMemberships[0];
      }
      if (target) {
        await switchTenant({ tenant_id: target.tenant_id });
        toast.success(`Back in ${target.tenant_name}`);
        try { sessionStorage.removeItem(DISMISS_KEY); } catch (_) {}
        navigate("/app/dashboard", { replace: true });
        setTimeout(() => window.location.reload(), 60);
        return;
      }
      // 3. Pure platform-only identity — clean logout + toast.
      await logout();
      toast("Signed out. Sign in again to open the platform admin console.");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
  };

  return (
    <div
      className="sticky top-0 z-40 flex items-center justify-between border-b border-amber-600/40 bg-amber-500 px-4 py-2 text-sm shadow-sm"
      style={{ backgroundColor: "#F59E0B", color: "#0A2540" }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
        <span>
          You're viewing as&nbsp;
          <b data-testid="impersonation-banner-tenant">{me?.current_tenant_name || "another workspace"}</b>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={exit}
          className="inline-flex items-center gap-1 rounded-md bg-[#0A2540] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0A2540]/90 disabled:opacity-50"
          data-testid="impersonation-banner-exit"
        >
          <LogOut className="h-3.5 w-3.5" /> Return to your account
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="grid h-6 w-6 place-items-center rounded-md hover:bg-black/10"
          data-testid="impersonation-banner-dismiss"
          title="Hide for this session"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
